import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import heroImage from "@/assets/ritual-hero.jpg?format=webp";

const totalCommissions = 3;
const remaining = 2;
const season = "Autumn/Winter 2026";

const HeroSection = () => {
  const { t } = useTranslation();

  return (
    <section className="relative w-full min-h-[100svh] flex items-center md:items-end overflow-hidden">
      {/* Background image */}
      <motion.div
        initial={{ scale: 1.08 }}
        animate={{ scale: 1 }}
        transition={{ duration: 2.4, ease: [0.25, 0.1, 0.25, 1] }}
        className="absolute inset-0"
      >
        <img
          src={heroImage}
          alt="Bespoke crochet wedding dress by Ritual"
          fetchPriority="high"
          loading="eager"
          decoding="async"
          className="w-full h-full object-cover"
        />
        {/* Adjusted gradient for better text legibility on mobile center */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 md:via-black/20 to-transparent" />
      </motion.div>

      {/* Content overlay */}
      <div className="relative z-10 w-full px-6 py-20 md:pt-0 md:pb-24 lg:pb-32 flex flex-col md:flex-row md:items-end justify-between gap-8">

        {/* Left — main hero text */}
        <div className="max-w-3xl shrink-0">
          <motion.div
            initial={{ opacity: 1, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          >
            <div className="w-12 h-px bg-white/60 mb-6" />
            <p className="font-body text-[10px] md:text-xs tracking-[0.3em] uppercase text-white/70 mb-4">
              {t("hero.title")}
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.2] md:leading-[1.1] mb-6">
              {t("hero.subtitle")}<br /><span className="italic font-serif opacity-90">&nbsp;</span>
            </h1>
            <p className="font-body text-sm md:text-base text-white/90 font-light max-w-md mb-8 leading-relaxed">
              {t("hero.description")}
            </p>

            {/* Mobile-only scarcity block */}
            <div className="md:hidden mb-8 space-y-1">
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/75">
                {t("hero.welcoming")} {season}
              </p>
              <p className="font-body text-[10px] tracking-[0.2em] uppercase text-gold/80">
                {t("hero.remaining_other", { count: remaining })}
              </p>
            </div>
          </motion.div>

          {/* Action Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
            className="flex flex-col sm:flex-row gap-4 w-full"
          >
            <Link
              to="/consultation"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-4 bg-gold text-foreground font-body text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:brightness-110 transition-all duration-500"
            >
              <span className="sm:hidden">{t("hero.beginCta")}</span>
              <span className="hidden sm:inline">{t("hero.together")}</span>
            </Link>

            <Link
              to="/collection"
              className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-4 border border-white/40 text-white font-body text-[10px] sm:text-xs tracking-[0.15em] uppercase hover:bg-white/10 transition-all duration-500"
            >
              <span className="sm:hidden">{t("hero.viewSilhouettes")}</span>
              <span className="hidden sm:inline">{t("hero.viewPast")}</span>
            </Link>
          </motion.div>
        </div>

      {/* Loom Tracker Panel */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.2, ease: "easeOut" }}
        className="flex flex-row md:flex-col items-center md:items-end justify-between md:justify-end gap-4 pb-1 shrink-0 border-t border-white/10 pt-6 md:border-none md:pt-0"
      >
        {/* Season/Text Info */}
        <div className="flex flex-col md:items-end">
          <p className="font-body text-[8px] md:text-[9px] tracking-[0.2em] md:tracking-[0.3em] uppercase text-white/60">
            {t("hero.welcoming")}
          </p>
          <p className="font-body text-[8px] md:text-[9px] tracking-[0.2em] md:tracking-[0.3em] uppercase text-white/60">
            {season}
          </p>
        </div>

        <div className="flex items-center md:items-end gap-4">
          {/* Visual Bars */}
          <div className="flex items-end gap-1 md:gap-1.5">
            {Array.from({ length: totalCommissions }).map((_, i) => (
              <motion.div
                key={i}
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{
                  duration: 0.35,
                  delay: 2.4 + i * 0.05,
                  ease: "easeOut",
                }}
                style={{ originY: 1 }}
                className={`w-1 md:w-2 h-4 md:h-8 ${
                  i < totalCommissions - remaining
                    ? "bg-white/15"
                    : "bg-gold"
                }`}
              />
            ))}
          </div>

          {/* Remaining Count Number */}
          <div className="flex items-baseline gap-1.5 md:gap-2">
            <span className="font-display text-3xl md:text-5xl text-gold leading-none">
              {remaining}
            </span>
            <span className="font-body text-[8px] md:text-[9px] tracking-[0.1em] uppercase text-white/65 leading-tight">
              <span className="hidden md:inline">of {totalCommissions}</span>
              <br />
              remaining
            </span>
          </div>
        </div>
      </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
